import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";

import { resolveAnalyticsElement } from "@/lib/analytics/analyticsElement";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { cn } from "@/lib/utils";
import { buttonVariants } from "./button-variants";

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /**
     * Structured element id wiring click analytics: stamps the `data-analytics-*` marker
     * attributes and queues `fe_element_clicked` on click (unless disabled).
     */
    analyticsElementId?: AnalyticsElementId;
  };

function Button({
  className,
  variant,
  size,
  asChild = false,
  analyticsElementId,
  onClick,
  disabled,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  const analyticsElement = resolveAnalyticsElement(analyticsElementId);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (analyticsElement && !disabled) {
      analyticsElement.emitClick();
    }
    onClick?.(event);
  };

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...analyticsElement?.attributes}
      onClick={analyticsElement ? handleClick : onClick}
      disabled={disabled}
      {...props}
    />
  );
}

export { Button, buttonVariants };
